// src/pages/TicketRecall/index.tsx
// Trang "Thu hồi vé ế" (mục 13.3, REQUIRE_FIXBUGS.md) — công ty tự lập phiếu rút vé khỏi kho tổng,
// KHÔNG chọn đại lý. Layout bám theo pages/TicketDeliveryManagement/index.tsx, bỏ phần đại lý.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { getIssuancePlans } from 'helpers/fakebackend_helper';
import { AgentMap, buildAgentMap, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { ISSUANCE_PLAN_STATUS, IssuancePlan } from 'pages/IssuancePlan/types';
import {
  cancelTicketRecall,
  confirmTicketRecall,
  createTicketRecall,
  fetchTicketRecalls,
} from 'slices/ticketRecall/thunk';
import {
  clearFilters,
  closeModal,
  openModal,
  setFilters,
  setPage,
  setSelectedItem,
} from 'slices/ticketRecall/reducer';
import { useBrandingTitle } from '../../hooks';

import TicketRecallList from './components/TicketRecallList';
import CreateTicketRecallModal from './modals/CreateTicketRecallModal';
import RecallActionConfirmModal, { RecallActionType } from './modals/RecallActionConfirmModal';
import RecallDetailModal from './modals/RecallDetailModal';
import { CreateTicketRecallPayload, TICKET_RECALL_STATUS_OPTIONS, TicketRecall } from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const TicketRecallManagement: React.FC = () => {
  useBrandingTitle(st('thu_hoi_ve_e_text'));

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { list, selectedItem, pagination, filters, loading, submitting, modalState } = useSelector(
    (state: any) => state.TicketRecall,
  );

  // README.md mục 7 — mặc định hiển thị ĐẦY ĐỦ phiếu ở mọi trạng thái cho mọi loại tài khoản (kể cả
  // roleAdmin, trước đây chỉ mặc định lọc Draft — xem lịch sử ở slices/ticketRecall/reducer.ts);
  // role đại lý luôn chỉ thấy phiếu do chính mình tạo (BE tự ép theo agent đang đăng nhập, xem
  // GetPaginatedTicketRecallsQuery), không cần set filter riêng nữa vì initialFilters.status đã là
  // 'All'. Nút Xác nhận/Huỷ vẫn chỉ hiện với phiếu Draft, các trạng thái khác chỉ xem (view-only) —
  // xem isDraft trong components/TicketRecallList.tsx.
  // Danh sách kế hoạch phát hành đã Accept (đã nhập kho) — truyền vào modal Lập phiếu để chọn
  // ngay trong đó, cùng cách làm với CreateTicketDeliveryModal (mục 12).
  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  // Danh sách đại lý — dùng cho dropdown lọc "Nguồn" + map agentId -> tên khi hiển thị (DTO ticket-
  // service chỉ trả agentId, xem helpers/agentLookup.ts) + truyền vào modal Lập phiếu để chọn đại
  // lý cần thu hồi (bỏ trống = thu hồi từ kho tổng, như thiết kế ban đầu).
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [actionType, setActionType] = useState<RecallActionType>('confirm');

  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const planResponse = await getIssuancePlans({
          pageIndex: 1,
          pageSize: 200,
          status: ISSUANCE_PLAN_STATUS.Accept,
        });
        const planItems = getPayloadItems(planResponse) as IssuancePlan[];
        setPlans(planItems.filter((plan) => Number(plan.status) === ISSUANCE_PLAN_STATUS.Accept));
      } catch (error: any) {
        toast.error(error?.message || st('khong_the_tai_danh_sach_ke_message'));
      }

      setAgents(await loadAgentsWithCache());
    };

    loadReferenceData();
  }, []);

  const loadRecalls = useCallback(() => {
    dispatch(
      fetchTicketRecalls({
        pageIndex: pagination.currentPage,
        pageSize: pagination.pageSize,
        planId: filters.planId || undefined,
        agentId: filters.agentId || undefined,
        status: filters.status === 'All' ? undefined : filters.status,
      }) as any,
    );
  }, [dispatch, pagination.currentPage, pagination.pageSize, filters]);

  useEffect(() => {
    loadRecalls();
  }, [loadRecalls]);

  const handleCreate = () => {
    dispatch(openModal('create'));
  };

  const handleCreateSubmit = async (payload: CreateTicketRecallPayload) => {
    const result = await dispatch(createTicketRecall(payload) as any);
    if (createTicketRecall.fulfilled.match(result)) {
      toast.success(st('lap_phieu_thu_hoi_ve_e_message'));
      loadRecalls();
    } else {
      toast.error((result.payload as string) || st('khong_the_lap_phieu_thu_hoi_message'));
    }
  };

  const handleViewDetail = (recall: TicketRecall) => {
    dispatch(setSelectedItem(recall));
    dispatch(openModal('detail'));
  };

  const handleConfirmClick = (recall: TicketRecall) => {
    setActionType('confirm');
    dispatch(setSelectedItem(recall));
    dispatch(openModal('confirm'));
  };

  const handleCancelClick = (recall: TicketRecall) => {
    setActionType('cancel');
    dispatch(setSelectedItem(recall));
    dispatch(openModal('cancel'));
  };

  const handleActionConfirm = async (reason?: string) => {
    if (!selectedItem?.id) return;

    if (actionType === 'confirm') {
      const result = await dispatch(confirmTicketRecall(selectedItem.id) as any);
      if (confirmTicketRecall.fulfilled.match(result)) {
        toast.success(st('xac_nhan_thu_hoi_ve_e_message'));
        loadRecalls();
      } else {
        toast.error((result.payload as string) || st('khong_the_xac_nhan_thu_hoi_message'));
      }
      return;
    }

    const result = await dispatch(cancelTicketRecall({ id: selectedItem.id, reason }) as any);
    if (cancelTicketRecall.fulfilled.match(result)) {
      toast.success(st('huy_phieu_thu_hoi_ve_e_message'));
      loadRecalls();
    } else {
      toast.error((result.payload as string) || st('khong_the_huy_phieu_thu_hoi_message'));
    }
  };

  // clearFilters() của reducer reset về default chung (Tất cả trạng thái — xem initialFilters ở
  // slices/ticketRecall/reducer.ts), áp dụng như nhau cho mọi loại tài khoản.
  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const isActionModalOpen = modalState.confirm || modalState.cancel;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('thu_hoi_ve_e_text')} pageTitle={st('quan_ly_phat_hanh_text')} />

          <Card id="ticketRecallList">
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_sach_phieu_thu_hoi_ve_title')}</h5>
                </div>
                <div className="col-sm-auto d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-soft-secondary"
                    disabled={loading}
                    onClick={handleClearFilters}
                  >
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    {st('xoa_loc_button')}
                  </button>
                  {/* Sang màn quét QR chuyên dụng (số lượng lớn, nhiều ký hiệu — camera quét liên tục). */}
                  <button
                    type="button"
                    className="btn btn-soft-primary"
                    data-action-code="button-qr-scan"
                    data-action-name="Thu hồi vé bằng QR"
                    onClick={() => navigate('/ticket-recall/qr-scan')}
                  >
                    <i className="ri-qr-scan-2-line align-bottom me-1"></i>
                    {st('thu_hoi_bang_qr_button')}
                  </button>
                  {PageActionPermission.canDisplay(location.pathname, 'button-add') && (
                    <button
                      type="button"
                      className="btn btn-success add-btn"
                      data-action-code="button-add"
                      data-action-name="Lập phiếu thu hồi vé ế"
                      onClick={handleCreate}
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      {st('lap_phieu_thu_hoi_button')}
                    </button>
                  )}
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end mb-5">
                <Col md={3}>
                  <Label className="form-label">{st('ke_hoach_phat_hanh_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.planId}
                    onChange={(event) => dispatch(setFilters({ planId: event.target.value }))}
                  >
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planCode}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('nguon_thu_hoi_label')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.agentId}
                    onChange={(event) => dispatch(setFilters({ agentId: event.target.value }))}
                  >
                    <option value="">{st('tat_ca_nguon_option')}</option>
                    <option value="0">{st('kho_tong_text')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.code} - {agent.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.status}
                    onChange={(event) => dispatch(setFilters({ status: event.target.value }))}
                  >
                    {TICKET_RECALL_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3} className="text-md-end">
                  <span className="text-muted">
                    {st('tong_text')} <span className="fw-semibold">{pagination.totalItems}</span> {st('phieu_text')}
                  </span>
                </Col>
              </Row>

              <TicketRecallList
                recalls={list}
                pagination={pagination}
                agentMap={agentMap}
                loading={loading}
                onViewDetail={handleViewDetail}
                onConfirm={handleConfirmClick}
                onCancel={handleCancelClick}
              />

              <AgentPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={(page) => dispatch(setPage(page))}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {modalState.create && (
        <CreateTicketRecallModal
          isOpen={modalState.create}
          submitting={submitting}
          plans={plans}
          agents={agents}
          onClose={() => dispatch(closeModal('create'))}
          onSubmit={handleCreateSubmit}
        />
      )}

      {isActionModalOpen && (
        <RecallActionConfirmModal
          isOpen={isActionModalOpen}
          action={actionType}
          recall={selectedItem}
          agentMap={agentMap}
          loading={submitting}
          onClose={() => {
            dispatch(closeModal(actionType));
            dispatch(setSelectedItem(null));
          }}
          onConfirm={handleActionConfirm}
        />
      )}

      {modalState.detail && (
        <RecallDetailModal
          isOpen={modalState.detail}
          recall={selectedItem}
          agentMap={agentMap}
          onClose={() => {
            dispatch(closeModal('detail'));
            dispatch(setSelectedItem(null));
          }}
        />
      )}
    </React.Fragment>
  );
};

export default TicketRecallManagement;
